<template>
  <div>
    <app-spinner v-if="!is_loaded" />
    <template v-else>
      <h1 class="title">Suppliers</h1>
      <app-cells>
        <app-button @click.native="onOpenCreateModal">Create supplier</app-button>
        <app-input
          @input.native="onSupplierSearch"
          v-model="search"
          class="input--200"
          type="search"
          placeholder="Search by trade name"
          name="search"
        />
        <app-select
          @input="onChangeFilter"
          v-model="filter_group"
          :options="suppliers_groups_options"
          :reduce="item => item.id"
          placeholder="Group"
          class="select--200"
        />
        <app-select
          @input="onChangeFilter"
          v-model="filter_type"
          :options="suppliers_types_options"
          :reduce="item => item.code"
          placeholder="Type"
          class="select--200"
        />
        <VueCtkDateTimePicker
          id="date_picker_balance"
          class="custom-date-time-picker custom-date-time-picker--range"
          v-model="filter_dates"
          :custom-shortcuts="custom_shortcuts"
          @input="onInputDatePicker"
          @is-hidden="onHiddenDatePicker"
          format="YYYY-MM-DD"
          formatted="ll"
          label="Select dates"
          color="#77b017"
          button-color="#77b017"
          overlay
          no-header
          no-label
          locale="en"
          range
          ref="date_picker"
        />
        <app-filters
          @change="onChangeFilter"
          v-model="currency_filter"
          :filters="currency_filters"
          name="filterCurrency"
        />
      </app-cells>
      <app-cells>
        <app-filters
          @change="onChangeFilter"
          v-model="field_filter"
          :filters="field_filters"
          name="filterField"
        />
        <app-filters
          @change="onChangeFilter"
          v-model="order_filter"
          :filters="order_filters"
          name="filterOrder"
        />
      </app-cells>
      <v-client-table
        :data="table.items"
        :columns="table.columns"
        :options="table.options"
        class="table-default"
      >
        <template #h__trade_name><div class="table-default__left">Trade name</div></template>
        <template #trade_name="{ row }">
          <router-link :to="{ name: 'administrative-suppliers-detail-purchases', params: { id: row.id } }" class="table-default__link-border">
            {{ row.trade_name }}
          </router-link>
        </template>
        <template #efd_purchases="{ row }">
          {{ currency_filter === 'tzs' ? formatAmount(row.efd_purchases_tzs) : formatAmount(row.efd_purchases_usd) }}
        </template>
        <template #efd_sales="{ row }">
          {{ currency_filter === 'tzs' ? formatAmount(row.efd_sales_tzs) : formatAmount(row.efd_sales_usd) }}
        </template>
        <template #outcome="{ row }">
          {{ currency_filter === 'tzs' ? formatAmount(row.cashflow_outcome_tzs) : formatAmount(row.cashflow_outcome_usd) }}
        </template>
        <template #income="{ row }">
          {{ currency_filter === 'tzs' ? formatAmount(row.cashflow_income_tzs) : formatAmount(row.cashflow_income_usd) }}
        </template>
        <template #invoices="{ row }">
          {{ currency_filter === 'tzs' ? formatAmount(row.invoices_tzs) : formatAmount(row.invoices_usd) }}
        </template>
        <template #unpaid_pp="{ row }">
          {{ currency_filter === 'tzs' ? formatAmount(row.payment_plans_unpaid_tzs) : formatAmount(row.payment_plans_unpaid_usd) }}
        </template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            v-if="pagination.pages > 1"
            :clickHandler="onClickPagination"
            v-model="pagination.page"
            :page-count="pagination.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination.count" :count="table.items.length" :page="pagination.page" />
      </app-cells>
    </template>
    <modal
      name="create-modal"
      :width="700"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      draggable=".popup__title"
      class="modal-not-overflow"
    >
      <div class="popup">
        <button @click="$modal.hide('create-modal')" class="popup__close" type="button"></button>
        <div class="popup__title">Create supplier</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckForm">
            <app-grid>
              <template #item-1>
                <app-form-group label="Legal name" label-for="legal_name" required>
                  <app-input
                    v-model="form.legal_name"
                    id="legal_name"
                    placeholder="Legal name"
                    name="legal_name"
                    :error="$v.form.legal_name.$error"
                  />
                  <template #error>
                    <div v-if="$v.form.legal_name.$dirty && !$v.form.legal_name.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="Trade name" label-for="trade_name" required>
                  <app-input
                    v-model="form.trade_name"
                    id="trade_name"
                    placeholder="Trade name"
                    name="trade_name"
                    :error="$v.form.trade_name.$error"
                  />
                  <template #error>
                    <div v-if="$v.form.trade_name.$dirty && !$v.form.trade_name.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="Region">
                  <app-select
                    v-model="form.region"
                    :options="region_options"
                    :reduce="item => item.id"
                    placeholder="Region"
                  />
                </app-form-group>
                <app-form-group label="Group" required>
                  <app-select
                    v-model="form.group"
                    :options="suppliers_groups_options"
                    :reduce="item => item.id"
                    :clearable="false"
                    placeholder="Group"
                    :error="$v.form.group.$error"
                  />
                  <template #error>
                    <div v-if="$v.form.group.$dirty && !$v.form.group.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="Transaction item">
                  <app-select
                    v-model="form.transaction_item"
                    :options="transaction_items_options"
                    :reduce="item => item.id"
                    label="name_expense"
                    placeholder="Transaction item"
                  />
                </app-form-group>
              </template>
              <template #item-2>
                <app-form-group label="Type" required>
                  <app-select
                    v-model="form.type"
                    :options="suppliers_types_options"
                    :clearable="false"
                    placeholder="Type"
                    :error="$v.form.type.$error"
                  />
                  <template #error>
                    <div v-if="$v.form.type.$dirty && !$v.form.type.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group v-if="form.type?.tin" label="TIN" label-for="tin" required>
                  <app-input
                    v-model="form.tin"
                    id="tin"
                    placeholder="TIN"
                    name="tin"
                    :error="$v.form.tin.$error"
                  />
                  <template #error>
                    <div v-if="$v.form.tin.$dirty && !$v.form.tin.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group v-if="form.type?.vat" label="VAT" class="form-group--center">
                  <app-checkbox
                    v-model="form.vat"
                    label="VAT registered"
                    style="height: 28px;align-items: flex-start"
                  />
                </app-form-group>
                <app-form-group v-if="form.vat" label="VRN" label-for="vrn" required>
                  <app-input
                    v-model="form.vrn"
                    id="vrn"
                    placeholder="VRN"
                    name="vrn"
                    :error="$v.form.vrn.$error"
                  />
                  <template #error>
                    <div v-if="$v.form.vrn.$dirty && !$v.form.vrn.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="Territory" required class="form-group--mh">
                  <app-cells :indent="false">
                    <app-radio
                      v-for="radio of tax_zones_options"
                      :key="radio.name"
                      v-model="form.territory"
                      :value="radio.code"
                      name="tax_zones"
                      :checked="form.territory === radio.code"
                    >
                      <template #label>{{ radio.name }}</template>
                    </app-radio>
                  </app-cells>
                  <template #error>
                    <div v-if="$v.form.territory.$dirty && !$v.form.territory.required">Required field</div>
                  </template>
                </app-form-group>
              </template>
            </app-grid>
            <app-form-group label="Notes" label-for="notes">
              <app-textarea
                v-model="form.notes"
                rows="5"
                placeholder="Write a note"
                id="notes"
              />
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button :disabled="$v.form.$error" type="submit" ref="submit">Create supplier</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  getAdministrativeSuppliersFullList,
  getAdministrativeSuppliersGroups,
  getAdministrativeSuppliersTypes,
  getFinanceTaxZones,
  getFinanceTransactionsItemsList,
  getLocationRegions,
  postAdministrativeSupplierFull,
} from '@/http'
import debounce from 'lodash/debounce'
import { required, requiredIf } from 'vuelidate/lib/validators'
import VueCtkDateTimePicker from 'vue-ctk-date-time-picker'
import 'vue-ctk-date-time-picker/dist/vue-ctk-date-time-picker.css'

export default {
  name: 'SuppliersList',
  components: {VueCtkDateTimePicker},
  data() {
    return {
      is_loaded: false,
      filter_dates: null,
      custom_shortcuts: [
        {key: 'last7Days', label: 'Last 7 days', value: 7},
        {key: 'last30Days', label: 'Last 30 days', value: 30},
        {key: 'last60Days', label: 'Last 60 days', value: 60},
        {key: 'last90Days', label: 'Last 90 days', value: 90},
        {key: 'last180Days', label: 'Last 180 days', value: 180},
        {key: 'thisYear', label: 'This year', value: 'year'},
        {key: 'lastYear', label: 'Last year', value: '-year'},
      ],
      currency_filter: 'tzs',
      currency_filters: [
        { value: 'tzs', label: 'TZS' },
        { value: 'usd', label: 'USD' },
      ],
      field_filter: 'efd_purchases_',
      field_filters: [
        { value: 'efd_purchases_', label: 'EFD Purchases' },
        { value: 'efd_sales_', label: 'EFD Sales' },
        { value: 'cashflow_outcome_', label: 'Outcome' },
        { value: 'cashflow_income_', label: 'Income' },
        { value: 'invoices_', label: 'Invoices' },
        { value: 'payment_plans_unpaid_', label: 'Unpaid PP' },
      ],
      order_filter: '-',
      order_filters: [
        { value: '', label: 'Asc' },
        { value: '-', label: 'Desc' },
      ],
      table: {
        items: [],
        columns: ['trade_name', 'efd_purchases', 'efd_sales', 'outcome', 'income', 'invoices', 'unpaid_pp'],
        options: {
          headings: {
            trade_name: 'Trade name',
            efd_purchases: 'EFD Purchases',
            efd_sales: 'EFD Sales',
            outcome: 'Outcome',
            income: 'Income',
            invoices: 'Invoices',
            unpaid_pp: 'Unpaid PP',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination: {
        pages: 1,
        page: 1,
        count: 1,
      },
      search: '',
      filter_group: '',
      filter_type: '',
      suppliers_groups_options: [],
      suppliers_types_options: [],
      region_options: [],
      tax_zones_options: [],
      transaction_items_options: [],
      form: {},
    }
  },
  validations: {
    form: {
      type: { required },
      group: { required },
      legal_name: { required },
      trade_name: { required },
      territory: { required },
      tin: {
        required: requiredIf(function() {
          return this.form.type?.tin
        })
      },
      vrn: {
        required: requiredIf(function() {
          return this.form.vat
        })
      },
    }
  },
  created() {
    Promise.all([
      getAdministrativeSuppliersGroups(),
      getAdministrativeSuppliersTypes(),
      getLocationRegions(),
      getFinanceTaxZones(),
      getFinanceTransactionsItemsList({expense: true}),
      this.fetchItems()
    ])
      .then(response => {
        this.suppliers_groups_options = response[0].data
        this.suppliers_types_options = response[1].data
        this.region_options = response[2].data
        this.tax_zones_options = response[3].data
        this.transaction_items_options = response[4].data
        this.is_loaded = true
      })
  },
  methods: {
    async fetchItems() {
      const response = await getAdministrativeSuppliersFullList({
        page: this.pagination.page,
        page_size: 20,
        search: this.search,
        group: this.filter_group,
        type: this.filter_type,
        from_date: this.filter_dates?.start,
        to_date: this.filter_dates?.end,
        ordering: `${this.order_filter}${this.field_filter}${this.currency_filter}`,
      })
      this.table.items = response.data.results
      this.pagination.pages = response.data.pages
      this.pagination.count = response.data.count
    },
    onCheckForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.submit.preload = true
      if (!this.form.vat) delete this.form.vrn
      postAdministrativeSupplierFull({ ...this.form, type: this.form.type.code })
        .then(() => {
          this.fetchItems()
          this.$notify({
            type: 'success',
            title: 'Supplier created',
          })
          this.$modal.hide('create-modal')
        })
        .catch(() => { this.$refs.submit.preload = false })
    },
    onClickPagination(page) {
      this.pagination.page = page
      this.fetchItems()
    },
    onOpenCreateModal() {
      this.form = {
        territory: 'mainland'
      }
      this.$v.form.$reset()
      this.$modal.show('create-modal')
    },
    onSupplierSearch() {
      this.searchSupplier(this)
    },
    searchSupplier: debounce(vm => {
      vm.pagination.page = 1
      vm.fetchItems()
    }, 350),
    onChangeFilter() {
      this.pagination.page = 1
      this.search = ''
      this.fetchItems()
    },
    onInputDatePicker() {
      if (!this.$refs.date_picker.pickerOpen) this.fetchItems()
    },
    onHiddenDatePicker() {
      if (this.filter_dates?.start) this.fetchItems()
    },
    formatAmount(amount) {
      return amount ? new Intl.NumberFormat('ru-RU', {minimumFractionDigits: 2}).format(amount) : '-'
    },
  }
}
</script>

<style scoped lang="sass">

</style>
